class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupRecommendations();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

   document.body.classList.add('overflow-hidden');
    this.scrollPosition = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
    this.renderRecommendations();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.scrollPosition || 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      document.dispatchEvent(new CustomEvent('cart-drawer-update'));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ---------------------------------------------------------------------
   * "Vaak samen gekocht met" (complementary recommendations)
   *
   * Haalt voor elk uniek product in het mandje complementary recommendations
   * op via Shopify's Recommendations API, en combineert die round-robin
   * gededupliceerd in de container in snippets/cart-drawer-recommendations.liquid.
   * ------------------------------------------------------------------- */

setupRecommendations() {
    // Gedebouncet omdat drempel-acties (bijv. een automatisch toegevoegd
    // gratis cadeau) meerdere cart-updates vlak na elkaar kunnen triggeren.
    // Zonder debounce annuleert elke nieuwe update de vorige, nog lopende
    // recommendations-fetch, waardoor er soms niets meer gerenderd wordt.
    const debouncedRender = debounce(() => this.renderRecommendations(), 400);

    document.addEventListener('cart-drawer-update', debouncedRender);

    // Dekt quantity-wijzigingen en verwijderen van items, die via een ander
    // pad lopen (CartItems.updateQuantity in cart.js) en geen 'cart-drawer-update'
    // event afvuren, alleen het theme-brede pubsub-signaal.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, debouncedRender);
    }
  }
  async renderRecommendations() {
    const container = this.querySelector('#CartDrawerRecommendations');
    if (!container) return;

    const productIds = (container.dataset.productIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (!productIds.length) {
      container.innerHTML = '';
      return;
    }

    // Voorkom race conditions bij snel achter elkaar wijzigende mandjes
    const requestToken = Symbol('cart-drawer-recommendations-request');
    this._recommendationsRequestToken = requestToken;

    const limit = container.dataset.limit || 5;

    try {
      const responses = await Promise.all(
        productIds.map((productId) =>
          fetch(
            `${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=cart-drawer-recommendations&product_id=${productId}&intent=complementary&limit=${limit}`
          )
            .then((res) => (res.ok ? res.text() : ''))
            .catch(() => '')
        )
      );

      // Als er ondertussen een nieuwer verzoek is gestart, negeer dit resultaat
      if (this._recommendationsRequestToken !== requestToken) return;

      const itemsPerProduct = responses.map((html) => {
        if (!html) return [];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('.cart-drawer-recommendations__item'));
      });

      const excludeIds = new Set(productIds);
      const seenIds = new Set();
      const merged = [];

      // Round-robin combineren zodat elk mandje-product evenredig bijdraagt
      let index = 0;
      let addedInLastPass = true;
      while (merged.length < limit && addedInLastPass) {
        addedInLastPass = false;
        for (const items of itemsPerProduct) {
          const el = items[index];
          if (!el) continue;
          addedInLastPass = true;
          const id = el.dataset.productId;
          if (excludeIds.has(id) || seenIds.has(id)) continue;
          seenIds.add(id);
          merged.push(el);
          if (merged.length >= limit) break;
        }
        index += 1;
      }

      container.innerHTML = '';
      merged.forEach((el) => container.appendChild(el));
    } catch (error) {
      // Stil falen: liever geen upsell tonen dan de cart drawer breken
      container.innerHTML = '';
    }
  }
}

customElements.define('cart-drawer', CartDrawer);


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);